/**
 * Arrival: within 500 ft of a paper's address, the screen becomes a dashboard
 * about that paper (history, dates, % now, past notes, safety alerts) and asks
 * "Are you attempting this paper now?". Yes → one-tap results screen.
 * Same component on the phone and the in-car laptop. Works offline (the attempt
 * is queued); the rules engine still decides which results are allowed.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addressKey,
  distanceFeet,
  formatVehicle,
  estimateService,
  evaluateAttempt,
  getPaperType,
  localParts,
  OUTCOME_LABELS,
  phaseOf,
  validateSubstituteRecipient,
  WINDOW_LABELS,
  type AttemptOutcome,
  type DayWindow,
  type PaperStatus,
  type RuleSetConfig,
} from '@civilos/domain';
import { ApiError } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDate, fmtDateTime, newId } from '../shared/format.ts';
import { ErrorBox, useAction } from '../shared/ui.tsx';
import { SafetyAlerts, unacknowledged } from './alerts.tsx';
import { useDuty } from './duty.tsx';
import { navigateUrl } from './FieldMap.tsx';
import { pinOf, wontCount, type Pos } from './position.ts';
import { submitAttempt } from './sync.ts';
import { VehiclesInYard, vehiclesPayload, type VehicleDraft } from './vehicles.tsx';

export const ARRIVAL_FEET = 500;
const RESET_FEET = 800; // after "No", pop up again only once you've left and come back
const MAX_ACCURACY_M = 150; // a fix worse than ~500 ft can't tell you're at the house

interface Row {
  p: any;
  ev: any;
}

const RECENT_MS = 60 * 60_000; // don't pop up again for a paper attempted in the last hour (survives a reload)
function recentlyAttempted(r: Row) {
  const list: { attemptedAt?: Date; attempted_at?: string }[] = r.ev?.attempts ?? r.p.attempts;
  return list.some((a) => Date.now() - +new Date(a.attemptedAt ?? a.attempted_at!) < RECENT_MS);
}

const attemptable = (p: any) => ['ASSIGNED', 'IN_SERVICE'].includes(p.status) && !p.onHold && !p.recalled;

/** The nearest open paper within 500 ft that hasn't been dismissed on this visit. */
export function useArrival(rows: Row[], pos: Pos | null) {
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const near = useMemo(() => {
    if (!pos || pos.accuracy > MAX_ACCURACY_M) return [];
    return rows
      .filter((r) => attemptable(r.p) && pinOf(r.p) && !recentlyAttempted(r))
      .map((r) => ({ ...r, feet: distanceFeet(pos, pinOf(r.p)!) }))
      .filter((r) => r.feet <= ARRIVAL_FEET)
      .sort((a, b) => a.feet - b.feet);
  }, [rows, pos?.lat, pos?.lng, pos?.accuracy]);
  // Forget a dismissal once the deputy is well away from that address.
  useEffect(() => {
    if (!pos || !dismissed.size) return;
    const keep = [...dismissed].filter((id) => {
      const r = rows.find((x) => x.p.id === id);
      const pin = r && pinOf(r.p);
      return pin && distanceFeet(pos, pin) < RESET_FEET;
    });
    if (keep.length !== dismissed.size) setDismissed(new Set(keep));
  }, [pos?.lat, pos?.lng]);
  const current = near.find((r) => !dismissed.has(r.p.id)) ?? null;
  return {
    current,
    near,
    dismiss: (id: string) => setDismissed((d) => new Set(d).add(id)),
  };
}

function countyLabel(name: string | null | undefined) {
  const n = (name ?? '').replace(/,.*$/, '').trim();
  if (!n) return 'our county';
  return /county$/i.test(n) ? n : `${n} County`;
}

type Choice = 'personal' | 'substitute' | 'no_answer' | 'gated_no_access' | 'vacant' | 'bad_address' | 'outside_service_area';

export function ArrivalScreen({
  row,
  others,
  rules,
  pending,
  pos,
  online,
  auto,
  onSwitch,
  onClose,
  onFullForm,
  onChanged,
}: {
  row: Row;
  others: Row[];
  rules: Record<string, RuleSetConfig>;
  pending: any[];
  pos: Pos | null;
  online: boolean;
  auto: boolean;
  onSwitch: (paperId: string) => void;
  onClose: () => void;
  onFullForm: () => void;
  onChanged: () => void;
}) {
  const { me } = useAuth();
  const duty = useDuty();
  const p = row.p;
  const [stage, setStage] = useState<'dashboard' | 'result' | 'done'>('dashboard');
  const [doneMsg, setDoneMsg] = useState('');
  const [sceneErr, setSceneErr] = useState<string | null>(null);
  useEffect(() => {
    setStage('dashboard');
    setSceneErr(null);
  }, [p.id]);

  const tz = me?.agency.timezone ?? 'America/New_York';
  const now = new Date();
  const est = estimateService(p.serveStats, now, tz);
  const cfg = rules[p.ruleVersionId];
  const pin = pinOf(p);
  const feet = pin && pos ? Math.round(distanceFeet(pos, pin)) : null;
  const unread = unacknowledged(p);
  const mustRead = online && unread.length > 0;
  const addr = [p.address.line1, p.address.line2, p.address.city].filter(Boolean).join(', ');

  const yes = async () => {
    setSceneErr(null);
    // "Attempting now" = out of the car at this address, so dispatch knows and the safety timer runs.
    if (duty.duty?.onDuty && online) {
      await duty.call('/api/field/on-scene', { paperId: p.id }).catch((e) => setSceneErr(e instanceof ApiError ? e.message : 'Couldn’t tell dispatch you’re out of the car.'));
    }
    setStage('result');
  };
  const notAttempting = async () => {
    if (duty.duty?.status === 'on_scene' && duty.duty.onScene?.paperId === p.id && online) await duty.call('/api/field/status', { status: 'available' }).catch(() => undefined);
    onClose();
  };

  if (stage === 'done')
    return (
      <div className="arr arr-done">
        <div className="arr-done-box">
          <div className="arr-done-check">✓</div>
          <h1>{doneMsg}</h1>
          <p className="muted">{addr}</p>
          <button className="arr-yes" onClick={onClose}>
            Back to route
          </button>
        </div>
      </div>
    );

  return (
    <div className="arr">
      <header className="arr-top">
        <div className="arr-where">
          <span className="arr-k">{auto ? `YOU’RE AT THIS STOP${feet != null ? ` · ${feet} ft` : ''}` : 'ATTEMPT SCREEN'}</span>
          <strong>{addr}</strong>
        </div>
        <div className="arr-top-act">
          <a className="btn" href={navigateUrl(p)} target="_blank" rel="noreferrer">
            Navigate
          </a>
          <button className="btn" onClick={stage === 'result' ? notAttempting : onClose}>
            {stage === 'result' ? 'Cancel' : 'Back to route'}
          </button>
        </div>
      </header>
      {others.length > 0 && (
        <div className="arr-others">
          Also here:{' '}
          {others.map((o) => (
            <button key={o.p.id} className="btn sm" onClick={() => onSwitch(o.p.id)}>
              {o.p.defendant.full_name}
            </button>
          ))}
        </div>
      )}

      {stage === 'dashboard' ? (
        <Dashboard row={row} est={est} tz={tz} mustRead={mustRead} online={online} onChanged={onChanged} />
      ) : (
        <Results
          row={row}
          cfg={cfg}
          pending={pending}
          pos={pos}
          online={online}
          county={countyLabel(me?.agency.countyName)}
          onFullForm={onFullForm}
          onDone={(msg) => {
            setDoneMsg(msg);
            setStage('done');
            onChanged();
            duty.refresh();
          }}
        />
      )}

      {stage === 'dashboard' && (
        <div className="arr-ask">
          <h2>Are you attempting this paper now?</h2>
          {mustRead && <p className="arr-warn">Read the safety alert{unread.length > 1 ? 's' : ''} above first.</p>}
          {!attemptable(p) && <p className="arr-warn">This paper is {p.onHold ? 'on hold' : p.recalled ? 'recalled' : p.status.toLowerCase()} — no attempts.</p>}
          <div className="arr-yn">
            <button className="arr-yes" disabled={mustRead || !attemptable(p)} onClick={yes}>
              YES
            </button>
            <button className="arr-no" onClick={onClose}>
              NO
            </button>
          </div>
          {duty.duty?.onDuty && online && <p className="small muted">Yes also tells dispatch you’re out of the car here.</p>}
          {sceneErr && <p className="arr-warn">{sceneErr}</p>}
        </div>
      )}
    </div>
  );
}

function Dashboard({ row, est, tz, mustRead, online, onChanged }: { row: Row; est: ReturnType<typeof estimateService>; tz: string; mustRead: boolean; online: boolean; onChanged: () => void }) {
  const p = row.p;
  const ev = row.ev;
  const s = p.serveStats;
  const windows: DayWindow[] = ['morning', 'afternoon', 'evening'];
  const best = s
    ? windows.filter((w) => s[w].n > 0).sort((a, b) => s[b].personal / s[b].n - s[a].personal / s[a].n || s[b].n - s[a].n)[0]
    : undefined;
  const history = [
    ...p.attempts.map((a: any) => ({ at: a.attempted_at, outcome: a.outcome, method: a.method, notes: a.notes, doorHanger: a.door_hanger, vehicles: a.vehicles ?? [], who: 'this paper', here: true })),
    ...(p.history ?? []).map((a: any) => ({ at: a.attempted_at, outcome: a.outcome, method: a.method, notes: a.notes, doorHanger: a.door_hanger, vehicles: a.vehicles ?? [], who: `${a.server_name} · ${a.civilos_number} · ${a.line1}`, here: false })),
  ].sort((a, b) => +new Date(b.at) - +new Date(a.at));
  const blockers = ev?.attemptNow?.blockers ?? [];
  const progress = ev?.nonService;
  return (
    <div className="arr-grid">
      <section className="arr-card arr-paper">
        <div className="arr-name">{p.defendant.full_name}</div>
        <div className="muted">
          {p.evaluation?.paperType?.name} · <span className="mono">{p.civilosNumber}</span>
        </div>
        <dl className="arr-dl">
          <dt>Case</dt>
          <dd>{p.case?.case_number}{p.case?.plaintiff ? ` — ${p.case.plaintiff}` : ''}</dd>
          {p.case?.court_name && (
            <>
              <dt>Court</dt>
              <dd>{p.case.court_name}</dd>
            </>
          )}
          <dt>Court date</dt>
          <dd>{p.courtDate ? fmtDate(p.courtDate) : '—'}</dd>
          <dt>Attempts</dt>
          <dd>
            {p.attempts.length} on this paper
            {progress && !progress.satisfied && progress.missing?.length ? ` · still needed: ${progress.missing.join('; ')}` : ''}
          </dd>
          <dt>Right now</dt>
          <dd className={blockers.length ? 'bad' : wontCount(ev) ? 'warn' : 'ok'}>
            {blockers.length ? `Not eligible — ${blockers[0].message}` : wontCount(ev) ? (
              <>
                Non-qualified attempt — you can attempt, but it won’t count toward non-service: {wontCount(ev)}.
              </>
            ) : (
              'Eligible to attempt — qualified attempt'
            )}
          </dd>
          {p.defendant?.phone && (
            <>
              <dt>Phone</dt>
              <dd>
                <a href={`tel:${p.defendant.phone.replace(/[^0-9+]/g, '')}`}>{p.defendant.phone}</a>
              </dd>
            </>
          )}
          {p.defendant?.vehicle_info && (
            <>
              <dt>Vehicle</dt>
              <dd>{p.defendant.vehicle_info}</dd>
            </>
          )}
          {p.defendant?.employer && (
            <>
              <dt>Employer</dt>
              <dd>
                {p.defendant.employer}
                {p.defendant.employer_address ? ` — ${p.defendant.employer_address}` : ''}
              </dd>
            </>
          )}
        </dl>
        {p.specialInstructions && <div className="arr-instr">{p.specialInstructions}</div>}
      </section>

      {(p.customerNotes ?? []).length > 0 && (
        <section className="arr-card arr-cnotes">
          <div className="arr-k">NOTES FROM THE LAW OFFICE / COURT</div>
          {p.customerNotes.map((n: any, i: number) => (
            <div key={i}>
              {n.body} <span className="small muted">({n.author})</span>
            </div>
          ))}
        </section>
      )}

      {(p.doorHangerResponses ?? []).length > 0 && (
        <section className="arr-card arr-dh">
          <div className="arr-k">DOOR HANGER RESPONSE</div>
          {p.doorHangerResponses.map((d: any, i: number) => (
            <div key={i} className="arr-dhrow">
              <div>
                <strong>{d.name || 'No name given'}</strong>
                {d.phone && (
                  <>
                    {' '}
                    · <a href={`tel:${String(d.phone).replace(/[^0-9+]/g, '')}`}>{d.phone}</a>
                  </>
                )}
              </div>
              {d.bestTime && <div>Best time: {d.bestTime}</div>}
              {d.message && <div className="small">“{d.message}”</div>}
              <div className="small muted">{new Date(d.receivedAt).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
            </div>
          ))}
        </section>
      )}

      {(p.addressFlags ?? []).length > 0 && (
        <section className="arr-card arr-addrflags">
          <div className="arr-k">⚠ ADDRESS HISTORY</div>
          {p.addressFlags.map((f: any, i: number) => (
            <div key={i} className="arr-addrflag">
              {f.text}
            </div>
          ))}
        </section>
      )}

      <section className="arr-card arr-pct">
        <div className="arr-k">CHANCE OF SERVICE RIGHT NOW</div>
        <div className="arr-pcts">
          <div>
            <span className="arr-big">{est.personalPct == null ? 'NSH' : `${est.personalPct}%`}</span>
            <span className="arr-lbl">personal</span>
          </div>
          <div>
            <span className="arr-big">{est.substitutePct == null ? 'NSH' : `${est.substitutePct}%`}</span>
            <span className="arr-lbl">substitute</span>
          </div>
        </div>
        <p className="small muted">
          {est.basis === 'nsh'
            ? `Not enough service history (${est.n} past attempt${est.n === 1 ? '' : 's'}).`
            : `From ${est.n} past attempts on this person ${est.basis === 'window' ? WINDOW_LABELS[est.window] : 'at all times of day'}.`}
        </p>
        {s && s.total.n > 0 && (
          <table className="arr-win">
            <tbody>
              {windows.map((w) => (
                <tr key={w} className={w === est.window ? 'now' : ''}>
                  <td>{WINDOW_LABELS[w]}</td>
                  <td>{s[w].n} tries</td>
                  <td>{s[w].personal} personal</td>
                  <td>{s[w].substitute} sub</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {best && s![best].personal > 0 && <p className="small"><strong>Best time so far:</strong> {WINDOW_LABELS[best]}</p>}
      </section>

      <section className={`arr-card arr-safety ${(p.safetyAlerts ?? []).length ? 'hot' : ''} ${mustRead ? 'must' : ''}`}>
        <div className="arr-k">SAFETY ALERTS</div>
        <SafetyAlerts paper={p} online={online} onChanged={onChanged} compact />
      </section>

      <section className="arr-card arr-hist">
        <div className="arr-k">SERVICE HISTORY &amp; NOTES</div>
        {history.length === 0 && <p className="muted">No attempts on this person yet.</p>}
        <ul className="arr-hlist">
          {history.map((h, i) => (
            <li key={i}>
              <div className="arr-hrow">
                <strong>{fmtDateTime(h.at)}</strong>
                <span className={`arr-out ${h.outcome === 'served' ? 'ok' : ''}`}>
                  {OUTCOME_LABELS[h.outcome as AttemptOutcome]}
                  {h.method ? ` (${h.method})` : ''}
                  {h.doorHanger ? ' · door hanger left' : ''}
                </span>
              </div>
              {h.notes && <div className="arr-note">“{h.notes}”</div>}
              {h.vehicles.length > 0 && <div className="arr-veh">Vehicles: {h.vehicles.map((v: any) => formatVehicle(v)).join('; ')}</div>}
              <div className="arr-who">{h.here ? 'This paper' : h.who}</div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

const CHOICES: { id: Choice; label: string; sub?: string; tone: string }[] = [
  { id: 'personal', label: 'PERSONAL SERVICE', sub: 'handed to the defendant', tone: 'ok' },
  { id: 'substitute', label: 'SUBSTITUTE SERVICE', sub: 'left with someone who lives there', tone: 'ok' },
  { id: 'no_answer', label: 'NO ONE HOME', tone: 'plain' },
  { id: 'gated_no_access', label: 'UNABLE TO ACCESS', sub: 'locked gate', tone: 'plain' },
  { id: 'vacant', label: 'HOUSE IS VACANT', tone: 'stop' },
  { id: 'bad_address', label: 'ADDRESS DOES NOT EXIST', tone: 'stop' },
  { id: 'outside_service_area', label: 'NOT IN COUNTY', tone: 'stop' },
];

function Results({ row, cfg, pending, pos, online, county, onFullForm, onDone }: { row: Row; cfg: RuleSetConfig | undefined; pending: any[]; pos: Pos | null; online: boolean; county: string; onFullForm: () => void; onDone: (msg: string) => void }) {
  const p = row.p;
  const [choice, setChoice] = useState<Choice | null>(null);
  const [hanger, setHanger] = useState<boolean | null>(null);
  const [notes, setNotes] = useState('');
  const [vehicles, setVehicles] = useState<VehicleDraft[]>([]);
  const [r, setR] = useState({ name: '', relationship: '', age: '', estimated: true, residesWithDefendant: false, competent: false, notAParty: false });
  const [explain, setExplain] = useState('');
  const [needExplain, setNeedExplain] = useState(false);
  const clientId = useRef(newId());
  const a = useAction();
  const type = cfg ? getPaperType(cfg, p.paperTypeCode) : null;
  const attempts = row.ev?.attempts ?? p.attempts.map((x: any) => ({ id: x.id, attemptedAt: new Date(x.attempted_at), outcome: x.outcome, method: x.method }));

  const outcomeOf = (c: Choice): { outcome: AttemptOutcome; method: 'personal' | 'substitute' | null } =>
    c === 'personal' || c === 'substitute' ? { outcome: 'served', method: c } : { outcome: c, method: null };
  const check = (c: Choice) => {
    if (!cfg) return null;
    const o = outcomeOf(c);
    return evaluateAttempt(cfg, { paperTypeCode: p.paperTypeCode, phase: phaseOf(p.status as PaperStatus), onHold: p.onHold, recalled: p.recalled, attempts }, { at: new Date(), outcome: o.outcome, method: o.method, calendarOverride: p.calendarOverrideDate === localParts(new Date(), cfg.timezone).date });
  };
  const unavailable = (c: Choice): string | null => {
    if (c === 'substitute' && type && !type.substituteAllowed) return `${type.name}: personal service only`;
    const e = check(c);
    return e && !e.allowed ? e.blockers[0]?.message ?? 'Not allowed now' : null;
  };
  const recipient =
    choice === 'substitute'
      ? { name: r.name, relationship: r.relationship, age: r.estimated ? null : Number(r.age) || null, estimatedAge: r.estimated ? Number(r.age) || null : null, residesWithDefendant: r.residesWithDefendant, competent: r.competent, notAParty: r.notAParty }
      : null;
  const subIssues = type && choice === 'substitute' ? validateSubstituteRecipient(type, recipient) : [];
  const warnings = choice ? check(choice)?.warnings ?? [] : [];
  const canSave = !!choice && !unavailable(choice) && (choice !== 'no_answer' || hanger !== null) && subIssues.length === 0 && (!needExplain || explain.trim());

  const save = () =>
    a.run(async () => {
      const o = outcomeOf(choice!);
      const payload: Record<string, unknown> = {
        clientId: clientId.current,
        attemptedAt: new Date().toISOString(),
        outcome: o.outcome,
        method: o.method ?? undefined,
        recipient: recipient ?? undefined,
        notes: notes.trim() || undefined,
        doorHanger: choice === 'no_answer' ? hanger : undefined,
        vehicles: vehiclesPayload(vehicles),
        gps: pos ? { lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy } : undefined,
        serverExplanation: explain.trim() || undefined,
      };
      try {
        const res = await submitAttempt(p.id, p.version, payload);
        const label = CHOICES.find((x) => x.id === choice)!.label.toLowerCase();
        onDone(res.mode === 'queued' ? `Saved on this device (${label}) — uploads when you’re back online` : o.outcome === 'served' ? `Served — ${o.method} service recorded` : `Recorded: ${label === 'not in county' ? `not in ${county}` : label}`);
      } catch (e) {
        if (e instanceof ApiError && e.details?.code === 'GPS_ANOMALY_EXPLANATION_REQUIRED') setNeedExplain(true);
        throw e;
      }
    });

  return (
    <div className="arr-results" data-unsaved={choice || notes ? 'true' : undefined}>
      <div className="arr-rhead">
        <h2>Result for {p.defendant.full_name}</h2>
        {!online && <span className="arr-offline">Offline — will be saved on this device</span>}
      </div>
      <div className="arr-choices" role="group" aria-label="Attempt result">
        {CHOICES.map((c) => {
          const why = unavailable(c.id);
          return (
            <button key={c.id} className={`arr-choice ${c.tone} ${choice === c.id ? 'active' : ''}`} disabled={!!why} title={why ?? ''} aria-pressed={choice === c.id} onClick={() => (setChoice(c.id), setHanger(null))}>
              {c.id === 'outside_service_area' ? `NOT IN ${county.toUpperCase()}` : c.label}
              <small>{why ?? c.sub ?? ''}</small>
            </button>
          );
        })}
        <button className="arr-choice more" onClick={onFullForm}>
          OTHER RESULT…
          <small>posting, moved, deceased, notes form</small>
        </button>
      </div>

      {choice && (
        <div className="arr-follow">
          {choice === 'personal' && <p className="arr-confirm">Served <strong>{p.defendant.full_name}</strong> in person at {p.address.line1}.</p>}
          {choice === 'substitute' && (
            <div className="arr-sub">
              <h3>Who was served?</h3>
              <div className="arr-subgrid">
                <label>
                  Name
                  <input value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} autoFocus />
                </label>
                <label>
                  Relationship to defendant
                  <input value={r.relationship} onChange={(e) => setR({ ...r, relationship: e.target.value })} placeholder="spouse, son, roommate…" />
                </label>
                <label>
                  Age
                  <span className="row gap-sm">
                    <input inputMode="numeric" value={r.age} onChange={(e) => setR({ ...r, age: e.target.value.replace(/\D/g, '') })} style={{ width: 80 }} />
                    <span className="row gap-sm small">
                      <input type="checkbox" checked={r.estimated} onChange={(e) => setR({ ...r, estimated: e.target.checked })} /> estimated
                    </span>
                  </span>
                </label>
              </div>
              <div className="arr-checks">
                <label>
                  <input type="checkbox" checked={r.residesWithDefendant} onChange={(e) => setR({ ...r, residesWithDefendant: e.target.checked })} /> Lives here with the defendant
                </label>
                <label>
                  <input type="checkbox" checked={r.competent} onChange={(e) => setR({ ...r, competent: e.target.checked })} /> Of competent mind
                </label>
                <label>
                  <input type="checkbox" checked={r.notAParty} onChange={(e) => setR({ ...r, notAParty: e.target.checked })} /> Not a party to the case
                </label>
              </div>
              {subIssues.length > 0 && (
                <ul className="issues">
                  {subIssues.map((i) => (
                    <li key={i.code} className="warn">
                      {i.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {choice === 'no_answer' && (
            <div className="arr-hanger">
              <h3>Was a door hanger left?</h3>
              <div className="arr-yn">
                <button className={`arr-yes ${hanger === true ? 'on' : ''}`} onClick={() => setHanger(true)} aria-pressed={hanger === true}>
                  YES
                </button>
                <button className={`arr-no ${hanger === false ? 'on' : ''}`} onClick={() => setHanger(false)} aria-pressed={hanger === false}>
                  NO
                </button>
              </div>
            </div>
          )}
          {['vacant', 'bad_address', 'outside_service_area'].includes(choice) && <p className="arr-confirm">This ends service on this paper as <strong>non-served</strong> — it goes back for the return.</p>}
          {warnings.length > 0 && (
            <ul className="issues">
              {warnings.map((w) => (
                <li key={w.code} className="warn">
                  {w.message}
                </li>
              ))}
            </ul>
          )}
          <VehiclesInYard value={vehicles} onChange={setVehicles} />
          <label className="arr-notes">
            Notes (optional)
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What you saw — cars, lights, who answered, best time to come back…" />
          </label>
          {needExplain && (
            <label className="arr-notes">
              GPS flagged — explain this attempt (required)
              <textarea rows={2} value={explain} onChange={(e) => setExplain(e.target.value)} />
            </label>
          )}
          <ErrorBox error={a.error} />
          <button className="arr-save" disabled={!canSave || a.busy} onClick={save}>
            {a.busy ? 'Saving…' : 'SAVE RESULT'}
          </button>
        </div>
      )}
    </div>
  );
}

/** Other open papers at the same address as `row` (two defendants in one house). */
export function sameAddress(rows: Row[], row: Row): Row[] {
  const k = addressKey(row.p.address);
  return rows.filter((r) => r.p.id !== row.p.id && attemptable(r.p) && addressKey(r.p.address) === k);
}

